import { DEFAULT_THEME, themeNames, type ThemeName } from "./generated/themes";

// jve-3-1b: theme switching seam. Setting data-fawkes-theme on <html> activates a theme's CSS
// overrides (generated/themes/<theme>.css); the default (no attribute / docasoft-green) is on :root.
// A theme-picker UI is a later shell concern — this is the programmatic switch + the font-loading
// note. Fonts are progressive: each theme's font token lists the web font first then a system
// fallback, so first paint never blocks on a web font (the browser swaps when the font arrives).
export function applyTheme(name: ThemeName): void {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-fawkes-theme", name);
  }
}

// Voorkeuren → Thema: the user's theme choice is persisted client-side (localStorage), the same way
// the locale is (i18nProvider) — no server round-trip. A future server-backed UserPreferences store
// can supersede this.
const THEME_STORAGE_KEY = "fawkes.theme";

/** The persisted theme choice, or the default when unset/invalid. */
export function storedTheme(): ThemeName {
  const value = globalThis.localStorage?.getItem(THEME_STORAGE_KEY);
  return value && (themeNames as string[]).includes(value) ? (value as ThemeName) : DEFAULT_THEME;
}

/** Persist + apply a theme choice. */
export function persistTheme(name: ThemeName): void {
  globalThis.localStorage?.setItem(THEME_STORAGE_KEY, name);
  applyTheme(name);
}

/** Apply the persisted theme — call once at startup so the choice survives a refresh. */
export function applyStoredTheme(): void {
  // Een eigen thema (uit een website afgeleid) staat niet in de gegenereerde lijst; het wordt uit
  // localStorage weer opgebouwd, anders zou je bij elke herlaadbeurt terugvallen op het standaardthema.
  if (globalThis.localStorage?.getItem(THEME_STORAGE_KEY) === EIGEN_THEMA) {
    const eigen = storedEigenPalet();
    if (eigen) {
      applyEigenThema(eigen);
      return;
    }
  }
  applyTheme(storedTheme());
}

export { DEFAULT_THEME, themeNames, type ThemeName };

// ── eigen thema (AI) ────────────────────────────────────────────────────────────────────────────
// Mijn voorkeuren → Thema → "Eigen thema": de api leidt vijf kleuren af uit een website
// (POST /api/thema/uit-website) en die worden hier tot een compleet palet uitgewerkt. De vaste thema's
// zijn CSS-bestanden; dit thema bestaat alleen bij deze gebruiker, dus het leeft als CSS-variabelen op
// <html> plus een kopie in localStorage — hetzelfde principe als de themakeuze zelf.

export interface EigenPalet {
  bron: string;
  primair: string;
  accent: string;
  achtergrond: string;
  oppervlak: string;
  tekst: string;
}

const EIGEN_KEY = "fawkes.theme.custom";

/** De naam waaronder het eigen thema is opgeslagen; geen CSS-bestand, dus buiten themeNames. */
export const EIGEN_THEMA = "eigen";

export function storedEigenPalet(): EigenPalet | null {
  const ruw = globalThis.localStorage?.getItem(EIGEN_KEY);
  if (!ruw) return null;
  try {
    const p = JSON.parse(ruw) as EigenPalet;
    return p && p.primair ? p : null;
  } catch {
    return null;
  }
}

/** #rrggbb → [r,g,b]; ongeldige invoer valt terug op zwart, zodat een fout nooit de app breekt. */
function rgb(hex: string): [number, number, number] {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [0, 0, 0];
  const n = Number.parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function hex([r, g, b]: [number, number, number]): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Twee kleuren mengen; `deel` is hoeveel van de tweede erin gaat. */
function meng(a: string, b: string, deel: number): string {
  const [r1, g1, b1] = rgb(a);
  const [r2, g2, b2] = rgb(b);
  return hex([r1 + (r2 - r1) * deel, g1 + (g2 - g1) * deel, b1 + (b2 - b1) * deel]);
}

function isDonker(kleur: string): boolean {
  const [r, g, b] = rgb(kleur);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.55;
}

/**
 * Van vijf kleuren naar de tokens die de app echt gebruikt. De varianten worden gemengd in plaats van
 * gevraagd: een site levert zelden een net setje hover- en soft-kleuren, en zelf mengen houdt ze
 * consistent met de kleur waar ze bij horen.
 */
function tokens(p: EigenPalet): Record<string, string> {
  const opTekst = isDonker(p.primair) ? "#ffffff" : "#1f2a24";
  return {
    "--fawkes-color-primary": p.primair,
    "--fawkes-color-primary-hover": meng(p.primair, "#000000", 0.15),
    "--fawkes-color-primary-soft": meng(p.primair, p.achtergrond, 0.85),
    "--fawkes-color-primary-fg": opTekst,
    "--fawkes-color-accent": p.accent,
    "--fawkes-color-bg": p.achtergrond,
    "--fawkes-color-surface": p.oppervlak,
    "--fawkes-color-surface-hover": meng(p.oppervlak, p.tekst, 0.04),
    "--fawkes-color-surface-muted": meng(p.oppervlak, p.tekst, 0.07),
    "--fawkes-color-text": p.tekst,
    "--fawkes-color-text-muted": meng(p.tekst, p.achtergrond, 0.35),
    "--fawkes-color-border": meng(p.achtergrond, p.tekst, 0.14),
    "--fawkes-color-border-strong": meng(p.achtergrond, p.tekst, 0.3),
  };
}

/** Het eigen thema tonen (zonder op te slaan) — de live preview van de themakaart. */
export function applyEigenThema(p: EigenPalet): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-fawkes-theme", EIGEN_THEMA);
  const stijl = document.documentElement.style;
  for (const [naam, waarde] of Object.entries(tokens(p))) {
    stijl.setProperty(naam, waarde);
  }
}

/** De losse variabelen weer weghalen, zodat een vast thema weer volledig geldt. */
export function clearEigenThema(): void {
  if (typeof document === "undefined") return;
  const stijl = document.documentElement.style;
  for (const naam of Object.keys(tokens({
    bron: "", primair: "#000000", accent: "#000000",
    achtergrond: "#ffffff", oppervlak: "#ffffff", tekst: "#000000",
  }))) {
    stijl.removeProperty(naam);
  }
}

/** Het eigen thema vastleggen als de keuze van deze gebruiker. */
export function persistEigenThema(p: EigenPalet): void {
  globalThis.localStorage?.setItem(EIGEN_KEY, JSON.stringify(p));
  globalThis.localStorage?.setItem("fawkes.theme", EIGEN_THEMA);
  applyEigenThema(p);
}
